import { computed, DestroyRef, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { OperatoryAppointmentV2, OperatoryAppointmentScheduleV2 } from '@ffd/api-client';
import { Store } from '@ngrx/store';
import { localStorageKey } from '@libs/utils';
import { AudioInputSelectorService } from '@libs/voice-input';
import { VolumeLevelService } from '@libs/shared-services';
import { selectCurrentUserProfile, selectSelectedPatientId } from '../../../../store/src';
import { AutoDraftState } from './auto-draft-model';
import { AutoDraftCoordinatorService } from './auto-draft-coordinator.service';
import { AutoCountdownDialogComponent, AutoCountdownDialogResult } from './components/auto-countdown-dialog';

export enum PromptType {
  Start,
}

interface Handler {
  type: PromptType;
  appointmentId: string;
}

const CHECK_INTERVAL = 10_000;
// 5 minutes without detected activity opens the countdown dialog.
const INACTIVITY_TIMEOUT_MS = 300_000;
// Volume at or above this counts as activity. The level is an RMS on a 0..128 scale, and a quiet room already reads
// above 1, which kept restarting the inactivity clock so AutoStop never fired (FW10-4323). 10 is the level the
// synthetic iOS meter uses for speaking.
const ACTIVITY_VOLUME_FLOOR = 10;
// The Start prompt already shows this long before the appointment starts, and then for the whole appointment.
const START_PROMPT_ADVANCE_WINDOW_MS = 300_000;

// Only a draft that was never recorded today invites the Start prompt. Paused and Stopped are both
// excluded: the user already made a decision about this draft, so re-prompting would be nagging. There
// is one autodraft per patient per day, so this state doubles as "today's draft has been dealt with".
const INACTIVE_STATES: Set<AutoDraftState> = new Set([AutoDraftState.Inactive]);
const ACTIVE_STATES: Set<AutoDraftState> = new Set([AutoDraftState.InProgress]);
const INACTIVITY_STATES: Set<AutoDraftState> = new Set([AutoDraftState.InProgress, AutoDraftState.Paused]);

@Injectable({ providedIn: 'root' })
export class AutoPromptService {
  private readonly coordinator = inject(AutoDraftCoordinatorService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);
  private readonly audioInputSelector = inject(AudioInputSelectorService);
  private readonly volumeLevelService = inject(VolumeLevelService);
  private readonly dialog = inject(Dialog);

  private readonly currentPatientId = this.store.selectSignal(selectSelectedPatientId);
  private readonly currentUserProfile = this.store.selectSignal(selectCurrentUserProfile);
  private readonly currentUserId = computed(() => this.currentUserProfile()?.id);

  private patientAppointmentCache = new Map<string, OperatoryAppointmentV2[]>();
  // eslint-disable-next-line unicorn/no-useless-undefined
  private readonly handler = signal<Handler | undefined>(undefined);

  private lastActivityTs = Date.now();
  private countdownDialogRef: DialogRef<AutoCountdownDialogResult> | undefined;

  public readonly showStartPrompt = computed(
    () => this.handler()?.type === PromptType.Start && INACTIVE_STATES.has(this.coordinator.autoDraftState()),
  );

  private get dismissedStartAppointments(): string[] {
    try {
      // Entries stored before the v2 (string id) switch are numbers, so normalise them to strings.
      return (JSON.parse(localStorage.getItem(localStorageKey.autoStartDismissed) ?? '[]') as unknown[]).map(String);
    } catch {
      return [];
    }
  }

  constructor() {
    effect(() => {
      const hasPatient = this.currentPatientId();

      untracked(() => {
        if (hasPatient || this.handler()?.type === PromptType.Start) {
          this.clearPrompt();
          this.checkAppointments();
        }
      });
    });

    effect(() => {
      if (this.coordinator.isAutodraftResolved()) {
        this.checkAppointments();
      }
    });

    // checkAppointments() is skipped while the overlay is open, so re-evaluate as soon as it closes.
    effect(() => {
      if (!this.coordinator.isOverlayOpen()) {
        untracked(() => this.checkAppointments());
      }
    });

    effect(() => {
      const level = this.volumeLevelService.mediaVolumeLevel();

      if (this.coordinator.autoDraftState() === AutoDraftState.InProgress && level >= ACTIVITY_VOLUME_FLOOR) {
        this.lastActivityTs = Date.now();
      }
    });

    // Recognised speech is activity as well, whatever the level meter reads.
    effect(() => {
      this.coordinator.liveTranscriptionText();

      untracked(() => {
        if (this.coordinator.autoDraftState() === AutoDraftState.InProgress) {
          this.lastActivityTs = Date.now();
        }
      });
    });

    // Entering a running state (start / resume / pause) starts a fresh inactivity clock; leaving one
    // dismisses any open countdown, since the draft is no longer waiting on the user.
    effect(() => {
      const isDraftActive = INACTIVITY_STATES.has(this.coordinator.autoDraftState());

      untracked(() => {
        if (isDraftActive) {
          this.lastActivityTs = Date.now();
        } else {
          this.closeCountdownDialog();
        }
      });
    });
  }

  public init(): void {
    const intervalId = setInterval(() => {
      this.checkAppointments();
      this.evaluateInactivity();
    }, CHECK_INTERVAL);
    this.destroyRef.onDestroy(() => clearInterval(intervalId));
  }

  public clearDismissedShowcasePrompts(showcaseAppointmentIds: string[]): void {
    const nonShowcaseIds = this.dismissedStartAppointments.filter((id) => !showcaseAppointmentIds.includes(id));

    try {
      localStorage.setItem(localStorageKey.autoStartDismissed, JSON.stringify(nonShowcaseIds));
    } catch (error) {
      console.error(error);
    }
  }

  public handleStartAutodraft(): void {
    this.coordinator.setIsDrawerStyle(false);
    this.coordinator.setIsDrawerOpen(false);
    this.coordinator.openOverlay();
    this.coordinator.commandStartVoiceTyping$.next(true);
    this.handleDismiss(PromptType.Start);
  }

  public handleOpenOverlay(): void {
    this.coordinator.setIsDrawerStyle(false);
    this.coordinator.setIsDrawerOpen(false);
    this.coordinator.openOverlay();
    // Only hide the prompt while the overlay is open. The appointment is not dismissed, so closing the
    // overlay without starting brings the Start appointment prompt back.
    this.clearPrompt();
  }

  public handleDismiss(type: PromptType): void {
    if (this.handler()?.appointmentId) {
      this.addDismissedVisit(this.handler()!.appointmentId, type);
    }

    this.handler.set(undefined);
  }

  public updateAppointments(operatoryAppointments: OperatoryAppointmentScheduleV2[]): void {
    const tempMap = new Map<string, OperatoryAppointmentV2[]>();

    operatoryAppointments
      .flatMap((op) => op.appointments)
      .forEach((appointment) => {
        if (!appointment.patientId) {
          return;
        }

        const prev = tempMap.get(appointment.patientId) ?? [];

        tempMap.set(appointment.patientId, [...prev, appointment]);
      });

    this.patientAppointmentCache = new Map(tempMap);
    this.checkAppointments();
  }

  private checkAppointments(): void {
    if (this.coordinator.isOverlayOpen()) {
      return;
    }

    // Fall back to the running autodraft's patient while it is active, so prompts keep working
    // after the user navigates away from the patient context page (FW10-3928).
    const patientId = this.currentPatientId() ?? this.activeAutodraftPatientId();

    if (!patientId) {
      return;
    }

    const appointments = this.patientAppointmentCache.get(patientId);

    if (!appointments?.length) {
      return;
    }

    const now = Date.now();

    for (const appointment of appointments) {
      const startMs = new Date(appointment.from).getTime();
      const endMs = new Date(appointment.to).getTime();

      if (startMs - START_PROMPT_ADVANCE_WINDOW_MS <= now && endMs >= now) {
        this.handleAppointmentStart(appointment);
        return;
      }
    }
  }

  // The inactivity countdown fires once a running draft has seen no activity for the timeout. It is
  // suppressed while the scheduled appointment is still ongoing (only after it ends, or with no
  // appointment at all, does silence mean the visit is likely over), and while a clinical note is open for editing.
  private evaluateInactivity(): void {
    if (this.countdownDialogRef) {
      return;
    }

    if (!INACTIVITY_STATES.has(this.coordinator.autoDraftState())) {
      return;
    }

    // Only the provider who started the draft may end it. For anyone else the draft is a read-only view, and AutoStop
    // would save the initiator's draft as a clinical note in their name.
    if (this.coordinator.isInitiatedByOther()) {
      return;
    }

    // While a clinical note is open for editing the user is still working, even without typing or speaking.
    if (this.coordinator.isClinicalNoteEditOpen()) {
      return;
    }

    const patientId = this.currentPatientId() ?? this.coordinator.autodraftPatientId();
    const now = Date.now();

    if (this.isWithinScheduledAppointment(patientId, now)) {
      return;
    }

    if (now - this.lastActivityTs < INACTIVITY_TIMEOUT_MS) {
      return;
    }

    this.openCountdownDialog();
  }

  private isWithinScheduledAppointment(patientId: string | undefined, now: number): boolean {
    if (!patientId) {
      return false;
    }

    const appointments = this.patientAppointmentCache.get(patientId);

    return (
      appointments?.some((appointment) => {
        const startMs = new Date(appointment.from).getTime();
        const endMs = new Date(appointment.to).getTime();

        return startMs <= now && endMs >= now;
      }) ?? false
    );
  }

  private openCountdownDialog(): void {
    this.countdownDialogRef = this.dialog.open<AutoCountdownDialogResult>(AutoCountdownDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      disableClose: true,
      maxWidth: '392px',
    });

    this.countdownDialogRef.closed.subscribe((result) => {
      this.countdownDialogRef = undefined;

      if (result === 'keepActive') {
        this.lastActivityTs = Date.now();
        return;
      }

      // 'end' (button) or 'expired' (no action) stops the draft, which saves the note and closes it.
      // A programmatic close (undefined result, e.g. the draft was already stopped elsewhere) does nothing.
      if (result === 'end' || result === 'expired') {
        this.coordinator.commandStopAndSaveNote$.next();
      }
    });
  }

  private closeCountdownDialog(): void {
    this.countdownDialogRef?.close();
    this.countdownDialogRef = undefined;
  }

  // Patient of the currently running autodraft, used as a fallback when no patient is selected
  // (e.g. after navigating away). Only returned while active so AutoStart stays scoped to the
  // patient context page.
  private activeAutodraftPatientId(): string | undefined {
    return ACTIVE_STATES.has(this.coordinator.autoDraftState()) ? this.coordinator.autodraftPatientId() : undefined;
  }

  private clearPrompt() {
    this.handler.set(undefined);
  }

  private handleAppointmentStart(appointment: OperatoryAppointmentV2): void {
    const isAlreadyShowing = this.handler()?.type === PromptType.Start && this.handler()?.appointmentId === appointment.id;

    if (
      this.coordinator.isInitiatedByOther() ||
      !this.validateAppointment(appointment) ||
      !INACTIVE_STATES.has(this.coordinator.autoDraftState()) ||
      this.coordinator.isAutodraftDataLoading() ||
      // The state only means "no draft for this patient" once the autodraft status is resolved;
      // before that (e.g. straight after a page refresh) it is simply not known yet (FW10-3999).
      !this.coordinator.isAutodraftResolved() ||
      isAlreadyShowing
    ) {
      return;
    }

    this.handler.set({
      type: PromptType.Start,
      appointmentId: appointment.id,
    });
  }

  private validateAppointment(appointment: OperatoryAppointmentV2): boolean {
    if (!this.hasUsableAudioInput()) {
      return false;
    }

    return !this.dismissedStartAppointments.includes(appointment.id);
  }

  /**
   * The Start prompt only needs to know that something can be recorded with, not which input is in use.
   *
   * Deliberately not selectedDevice(): that is null until the user explicitly picks an input from the dropdown,
   * because the platform default is used when nothing is stored. Gating on it silently suppressed both prompts
   * for everyone who never opened the dropdown (FW10 regression from the voice-input migration, where this
   * replaced ActiveTranscriptionService.getDeviceId(), which resolved to the default device id).
   */
  private hasUsableAudioInput(): boolean {
    return this.audioInputSelector.availableDevices().some((device) => device.isAvailable);
  }

  private addDismissedVisit(visitId: string, type: PromptType) {
    if (type !== PromptType.Start) {
      return;
    }

    try {
      const json = localStorage.getItem(localStorageKey.autoStartDismissed);

      const items: Set<string> = new Set(json ? (JSON.parse(json) as unknown[]).map(String) : []);
      items.add(visitId);

      localStorage.setItem(localStorageKey.autoStartDismissed, JSON.stringify([...items]));
    } catch (error) {
      console.error(error);
    }
  }
}
